// @givmo/app-admin — Approve / Reject join-request dialog (EP-SUITE2; the choice since EP-SUITE8).
//
// One dialog, mode driven by the `mode` prop, rendered by BOTH hosts: the employer portal's Admin
// Team join requests (`JoinRequestsSection`) and the company console's Join requests page, whose
// `AccessRequestActionDialog` renders this one under its own test-id prefix and label.
//   - APPROVE (EP-SUITE8, D-EP-69 — Jonathan's D-CPO-64: "they shouldn't be able to be approved if
//     nothing is checked"): the Global Admin chooses what the person can open — a Global Admin, or
//     exactly the apps ticked, each at a level — from every app `GET /admin/company/module-choices`
//     lists (on the console, those its build serves: EP-WEB55 (c), `servedApps`; the old portal
//     hands in none, D-EP-86), one the company has switched off marked and still choosable (D-EP-76
//     (2): the server takes it, and the grant waits for it to be turned on). NOTHING IS
//     PRE-CHECKED, Donations included, and Approve stays disabled until the choice is one to send
//     (`app-choice.ts`). Submit → `companiesApproveCompanyJoinRequestWithChoice`
//     (POST /admin/company/join-requests/{id}/approval) with EXACTLY `{role, modules: [{module,
//     role}]}`. The requester's `requested_app` is a NOTE above the choice, never a tick.
//   - REJECT: an OPTIONAL note. Submit → companiesRejectCompanyJoinRequest (POST …/{id}/reject) with
//     body { note } (blank collapses to undefined — the backend trims it to null).
//
// ⚠ ONLY THE NEW ROUTE, NEVER A FALLBACK. The old `/approve` grants the request's legacy product
// stamp; an older backend, which has no `/approval`, answers 404 and grants nothing, and this dialog
// says so and stops (the agreed OM-57 wire, §1). A refusal of the choice (the four
// `JOIN_APPROVAL_*` 422s) is said in words, never the server's sentence.
//
// ⚠ AFTER ANY ANSWER, RE-READ: `/approval` answers 200 for a request already approved without applying
// a different choice, so the dialog never repeats what it sent; it re-reads every join-request read
// and the host's roster (`rosterKeys`) and closes. A 404 or 409 also re-reads: the list is stale.

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sdk, isGivmoError, type CompanyJoinRequestOut } from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, Field, testIdProp } from '@givmo/design-system';
import {
  choiceBody,
  emptyAppChoice,
  readChoiceRefusal,
  requestedAppState,
  reseatAppChoice,
  type AppChoiceBody,
  type AppChoiceDraft,
} from '../admins/app-choice';
import { AppChoiceFields } from '../admins/app-choice-fields';
import { APPROVE_WORDS, approveRefusalWords, requestedAppNote } from '../admins/app-choice-words';
import { useAppChoices } from '../admins/use-app-choices';
import { requestedApp, requesterName } from './join-requests-decode';

export type JoinRequestActionMode = 'approve' | 'reject';

/** The prefix of every join-request read: either host's inbox and the Dashboard's pending count. */
export const JOIN_REQUESTS_READS = ['companies-list_company_join_requests'] as const;

export interface JoinRequestActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null when closed; otherwise which action the dialog performs. */
  mode: JoinRequestActionMode | null;
  /** The request being adjudicated; null when closed. */
  request: CompanyJoinRequestOut | null;
  /** The host's admin roster reads, re-read with every join-request read once the server answers. */
  rosterKeys?: readonly (readonly unknown[])[];
  /** The host's test-id prefix: `join-request` (the portal) or `access-request` (the console). */
  testIdPrefix?: string;
  /** What the host calls the request, for the dialog's accessible name ("join request"). */
  requestNoun?: string;
  /**
   * The apps this host's build serves, its app grid's set (EP-WEB55 (c)): the choice offers only
   * these. The console hands it in; absent (the old portal) ⇒ every app on the company's list.
   */
  servedApps?: readonly string[];
}

export function JoinRequestActionDialog({
  open,
  onOpenChange,
  mode,
  request,
  rosterKeys = [],
  testIdPrefix = 'join-request',
  requestNoun = 'join request',
  servedApps,
}: JoinRequestActionDialogProps) {
  const queryClient = useQueryClient();
  const isApprove = mode === 'approve';
  const prefix = testIdPrefix;

  const [draft, setDraft] = useState<AppChoiceDraft>(() => emptyAppChoice([]));
  const [note, setNote] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  const choices = useAppChoices(open && isApprove, servedApps);
  const rows = choices.rows;

  // Re-prime on each open: nothing chosen, no Global Admin, a blank note.
  useEffect(() => {
    if (!open) return;
    setDraft(emptyAppChoice([]));
    setNote('');
    setServerError(null);
  }, [open, mode, request]);

  // The list answers after the dialog opens (and again after a refusal re-reads it): re-seat, so
  // whatever has been chosen survives its arrival.
  useEffect(() => {
    if (!open || rows === null) return;
    setDraft((current) => reseatAppChoice(current, rows));
  }, [open, rows]);

  const reread = async () => {
    await Promise.all(
      [JOIN_REQUESTS_READS, ...rosterKeys].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
  };

  const approve = useMutation({
    mutationFn: (args: { requestId: string; body: AppChoiceBody }) =>
      sdk.companiesApproveCompanyJoinRequestWithChoice({
        path: { request_id: args.requestId },
        body: args.body,
        throwOnError: true,
      }),
    onSuccess: async () => {
      await reread();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      const refusal = readChoiceRefusal(error, 'JOIN_APPROVAL');
      setServerError(approveRefusalWords(refusal) ?? serverMessage(error));
      if (refusal?.kind === 'module-unavailable') choices.refetch();
      if (refusal?.kind === 'not-found' || (isGivmoError(error) && error.status === 409)) {
        void reread();
      }
    },
  });

  const reject = useMutation({
    mutationFn: (args: { requestId: string; note: string }) =>
      sdk.companiesRejectCompanyJoinRequest({
        path: { request_id: args.requestId },
        // Blank collapses to undefined so the body carries no note (backend trims to null).
        body: { note: args.note.trim().length > 0 ? args.note.trim() : undefined },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await reread();
      onOpenChange(false);
    },
    onError: (error: unknown) => setServerError(serverMessage(error)),
  });

  const submitting = approve.isPending || reject.isPending;
  const body = choiceBody(draft);

  const onSubmit = () => {
    if (request === null || mode === null) return;
    setServerError(null);
    if (mode === 'approve') {
      if (body === null) return;
      approve.mutate({ requestId: request.id, body });
    } else {
      reject.mutate({ requestId: request.id, note });
    }
  };

  const who = request ? requesterName(request) : '';
  const requested = requestedAppNote(requestedAppState(requestedApp(request), rows));

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !submitting && onOpenChange(next)}>
      <Dialog.Content
        aria-label={isApprove ? `Approve ${requestNoun}` : `Reject ${requestNoun}`}
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId={`${prefix}-action-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {isApprove ? `Approve ${who}` : `Reject ${who}`}
        </Dialog.Title>

        {isApprove ? (
          <>
            <p className="font-sans text-s14 text-text-tertiary">
              {APPROVE_WORDS.lead(who)}
              {requested !== null && (
                <>
                  {' '}
                  {/* The requester's own note: never a tick (D-CPO-64). */}
                  <span {...testIdProp(`${prefix}-requested-app`)}>
                    {requested.lead}
                    <span className="font-w600 text-text-secondary">{requested.app}</span>
                    {requested.tail}
                  </span>
                </>
              )}
            </p>
            <AppChoiceFields
              idPrefix={prefix}
              draft={draft}
              onDraftChange={setDraft}
              status={choices.status}
              onRetry={choices.refetch}
              disabled={submitting}
            />
          </>
        ) : (
          <Field
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            testId={`${prefix}-reject-note`}
          />
        )}

        {serverError !== null && (
          <ErrorMessage testId={`${prefix}-action-error`}>{serverError}</ErrorMessage>
        )}

        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={submitting}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp(`${prefix}-action-cancel`)}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone={isApprove ? 'primary' : 'danger'}
            size="md"
            loading={submitting}
            disabled={isApprove && body === null}
            onClick={onSubmit}
            testId={`${prefix}-action-submit`}
          >
            {isApprove ? 'Approve' : 'Reject'}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function serverMessage(error: unknown): string {
  return isGivmoError(error) ? error.message : 'Something went wrong. Please try again.';
}
